/**
 * The six shader programs of the original GameplayFootball renderer, ported from
 * desktop GLSL 1.50 to GLSL ES 3.00 for WebGL2.
 *
 * Source of truth is the twelve `.glsl` files next to this module; they are
 * imported verbatim with Vite's `?raw`. See README.md for the G-buffer contract,
 * the uniform inventory and every deviation from the original.
 */

import ambientFrag from './ambient.frag.glsl?raw';
import ambientVert from './ambient.vert.glsl?raw';
import lightingFrag from './lighting.frag.glsl?raw';
import lightingVert from './lighting.vert.glsl?raw';
import overlayFrag from './overlay.frag.glsl?raw';
import overlayVert from './overlay.vert.glsl?raw';
import postprocessFrag from './postprocess.frag.glsl?raw';
import postprocessVert from './postprocess.vert.glsl?raw';
import simpleFrag from './simple.frag.glsl?raw';
import simpleVert from './simple.vert.glsl?raw';
import zphaseFrag from './zphase.frag.glsl?raw';
import zphaseVert from './zphase.vert.glsl?raw';

export interface ShaderProgramSource {
  /** Program name as used by the original engine's `UseShader(...)`. */
  readonly name: ShaderProgramName;
  readonly vertex: string;
  readonly fragment: string;
  /**
   * Vertex attribute name -> location. These match the `layout(location = N)`
   * qualifiers in the sources, so no `bindAttribLocation` call is needed; the
   * table is here for the vertex-array setup code.
   */
  readonly attributes: Readonly<Record<string, number>>;
  /**
   * Fragment output name -> colour attachment index, matching the
   * `layout(location = N)` qualifiers.
   */
  readonly outputs: Readonly<Record<string, number>>;
  /** Sampler uniform name -> texture unit the original engine binds it to. */
  readonly samplers: Readonly<Record<string, number>>;
}

export type ShaderProgramName =
  | 'zphase'
  | 'simple'
  | 'ambient'
  | 'lighting'
  | 'postprocess'
  | 'overlay';

export const SHADER_PROGRAMS: Readonly<Record<ShaderProgramName, ShaderProgramSource>> = {
  zphase: {
    name: 'zphase',
    vertex: zphaseVert,
    fragment: zphaseFrag,
    attributes: { position: 0 },
    outputs: { out_discarded: 0 },
    samplers: {},
  },
  simple: {
    name: 'simple',
    vertex: simpleVert,
    fragment: simpleFrag,
    attributes: { position: 0, normal: 1, texcoord: 2, tangent: 3, bitangent: 4 },
    outputs: { out_albedo: 0, out_normal: 1, out_aux: 2 },
    samplers: { map_albedo: 0, map_normal: 1, map_specular: 2, map_illumination: 3 },
  },
  ambient: {
    name: 'ambient',
    vertex: ambientVert,
    fragment: ambientFrag,
    attributes: { position: 0 },
    outputs: { out_accumulation: 0, out_modifier: 1 },
    samplers: { map_albedo: 0, map_normal: 1, map_depth: 2, map_aux: 3, map_noise: 4 },
  },
  lighting: {
    name: 'lighting',
    vertex: lightingVert,
    fragment: lightingFrag,
    attributes: { position: 0 },
    outputs: { out_accumulation: 0, out_modifier: 1 },
    samplers: { map_albedo: 0, map_normal: 1, map_depth: 2, map_shadow: 7 },
  },
  postprocess: {
    name: 'postprocess',
    vertex: postprocessVert,
    fragment: postprocessFrag,
    attributes: { position: 0 },
    outputs: { out_color: 0 },
    samplers: { map_accumulation: 0, map_modifier: 1, map_depth: 2 },
  },
  overlay: {
    name: 'overlay',
    vertex: overlayVert,
    fragment: overlayFrag,
    attributes: { vertex: 0 },
    outputs: { out_color: 0 },
    samplers: { ourTexture: 0 },
  },
} as const;

export const SHADER_PROGRAM_NAMES = Object.keys(SHADER_PROGRAMS) as ShaderProgramName[];

/** Number of vec3 entries in `ambient`'s SSAO kernel uniform array. */
export const SSAO_KERNEL_SIZE = 32;
